import type { CorrectionAction, DriftDirection, DriftState } from './types';

const DRIFT_THRESHOLD_NONE = 250;
const DRIFT_THRESHOLD_SOFT = 500;
const DRIFT_HYSTERESIS = 100;
const SOFT_RATE_SLOW = 0.97;
const SOFT_RATE_FAST = 1.03;
const NORMAL_RATE = 1.0;

export class DriftCorrector {
  private _state: DriftState = 'synced';

  get state(): DriftState {
    return this._state;
  }

  evaluate(
    driftMs: number,
    direction: DriftDirection,
    estimatedDriverTimeMs: number,
  ): CorrectionAction {
    switch (this._state) {
      case 'synced':
        return this.evaluateSynced(driftMs, direction, estimatedDriverTimeMs);
      case 'soft_correction':
        return this.evaluateSoftCorrection(driftMs, direction, estimatedDriverTimeMs);
      case 'hard_correction':
        return { type: 'seek', targetTimeMs: estimatedDriverTimeMs };
    }
  }

  reset(): void {
    this._state = 'synced';
  }

  private evaluateSynced(
    driftMs: number,
    direction: DriftDirection,
    estimatedDriverTimeMs: number,
  ): CorrectionAction {
    if (driftMs < DRIFT_THRESHOLD_NONE) {
      return { type: 'none' };
    }
    if (driftMs <= DRIFT_THRESHOLD_SOFT) {
      this._state = 'soft_correction';
      return { type: 'adjust_rate', playbackRate: this.rateForDirection(direction) };
    }
    this._state = 'hard_correction';
    return { type: 'seek', targetTimeMs: estimatedDriverTimeMs };
  }

  private evaluateSoftCorrection(
    driftMs: number,
    direction: DriftDirection,
    estimatedDriverTimeMs: number,
  ): CorrectionAction {
    if (driftMs < DRIFT_HYSTERESIS) {
      this._state = 'synced';
      return { type: 'adjust_rate', playbackRate: NORMAL_RATE };
    }
    if (driftMs <= DRIFT_THRESHOLD_SOFT) {
      return { type: 'adjust_rate', playbackRate: this.rateForDirection(direction) };
    }
    this._state = 'hard_correction';
    return { type: 'seek', targetTimeMs: estimatedDriverTimeMs };
  }

  private rateForDirection(direction: DriftDirection): number {
    return direction === 'behind' ? SOFT_RATE_FAST : SOFT_RATE_SLOW;
  }
}
